/**
 * Generate a URL-friendly slug from a name.
 * Removes accents, special chars, replaces spaces with hyphens, lowercases.
 */
export const nameToSlug = (name: string): string =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove accents
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")   // remove special chars
    .trim()
    .replace(/\s+/g, "-")           // spaces → hyphens
    .replace(/-+/g, "-");           // collapse multiple hyphens

/**
 * Hardcoded collection/module ID → slug mapping.
 * These take priority over auto-generated slugs.
 */
export const COLLECTION_SLUG_MAP: Record<string, string> = {
  "a398cc78-eb3e-4525-a785-6033766c2b50": "panaderia",
  "f9597a7c-defb-4155-b98b-2b494bdebc38": "clean-bakes",
  "178baf8a-b2ad-4b57-bdf8-8c8b37a3e523": "reposteria-cero",
  "d9ff362a-8308-46d8-af96-c19865fe35d2": "clean-sweets",
  "fe1ecc53-fa43-4ad8-ba34-3b1665e04b3c": "cocina-real",
  "c2ad465f-64a5-4b17-adea-5d4d87e6b697": "clean-meals",
};

/** Reverse map: slug → collection/module ID */
export const SLUG_TO_COLLECTION_ID: Record<string, string> = Object.fromEntries(
  Object.entries(COLLECTION_SLUG_MAP).map(([id, slug]) => [slug, id])
);

/** Legacy/public slugs that must resolve to the canonical collection URLs. */
export const LEGACY_SLUG_REDIRECT_MAP: Record<string, string> = {
  // English-name aliases → canonical slugs (these are not standalone products)
  "zero-bakery":  "panaderia",
  "zero-pastry":  "reposteria-cero",
  "real-cuisine": "cocina-real",
  // Legacy slug for the renamed "Panadería Cero" → "Panadería" offer
  "panaderia-cero": "panaderia",
  // Unified product slugs — English variants now redirect to the canonical Spanish slug
  "750-healthy-recipes": "750-recetas",
  "fit-jams": "mermeladas-fit",
  "healthy-smoothies": "batidos-saludables",
  "premium-gluten-free-pastry": "reposteria-sin-tacc",
  "premium-italian-pasta": "pastas-italianas",
};

/** Returns the canonical public path for product/content slugs. */
export const getCanonicalContentPath = (slug?: string | null): string | null => {
  if (!slug) return null;
  return `/${LEGACY_SLUG_REDIRECT_MAP[slug] || slug}`;
};

/**
 * Build the path for a collection/oferta given its id and name.
 * Uses hardcoded slugs when available, otherwise generates from name.
 */
export const getCollectionPath = (id: string, name?: string): string => {
  if (COLLECTION_SLUG_MAP[id]) return `/${COLLECTION_SLUG_MAP[id]}`;
  if (name) return `/${nameToSlug(name)}`;
  return `/coleccion/${id}`;
};
